import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { replace } from '../../services/router-utils';
import { change, reset } from 'redux-form';
import classNames from 'classnames';
import { upload, TYPE_IMAGE } from '../../actions/upload';
import { cancelUploads } from '../../actions/cancel-uploads';
import { updateUser } from '../../actions/update-user';
import { pageOpened } from '../../actions/page-opened';
import { buttonClicked } from '../../actions/button-clicked';
import resizeImage from '../../services/resize-image';
import { getUserBySlug } from '../../selectors/user-selectors';
import { uploadStarted } from '../../actions/upload-started';
import { uploadEnded } from '../../actions/upload-ended';
import messages, {
  REQUIRE_PROFILE_FIELDS,
  PROFILE_UPDATE,
  PROFILE_UPDATE_FAILED,
} from '../../components/message/messages';
import Page from '../../components/page';
import PageMetadata from '../../components/page-metadata';
import FollowUserButtonSecure from '../../containers/follow-user-button-secure';
import Profile, { VIEW_PROFILE, VIEW_PROFILE_EDIT } from '../../components/profile';
import ProfilePageLayout from '../../components/profile-page-layout';
import ProfilePlaceholder from '../../components/profile-placeholder';
import Button from '../../components/button';
import PublicProfileActions from '../../containers/public-profile-actions';
import ProfileActionsButton from '../../components/profile-actions-button';
import AppLoaded from '../../components/app-loaded';
import withTranslate from '../../hoc/with-translate';
import { getCurrentUserSlug, isOwner } from '../../selectors/current-user-selectors';
import { USER_PROFILE, USER_EDIT_PROFILE } from '../../services/detect-route';
import profileStyles from '../../components/profile/profile.scss';
import withEnvironment from '../../hoc/with-environment';

export class ProfilePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      view: VIEW_PROFILE,
      user: props.user,
      isCurrentUser: props.isCurrentUser,
    };
  }

  componentDidMount = () => {
    this.props.pageOpened({ page: USER_PROFILE });
  };

  componentWillReceiveProps = ({ user, isCurrentUser }) => {
    if (!this.state.disabledUpdate) {
      this.setState({ user, isCurrentUser });
    }
  };

  componentDidUpdate = (props, state) => {
    if (state.view !== this.state.view) {
      if (this.state.view === VIEW_PROFILE_EDIT) {
        this.props.pageOpened({ page: USER_EDIT_PROFILE });
      } else {
        this.props.pageOpened({ page: USER_PROFILE });
      }
    }
  };

  setForm = ref => (this.form = ref);

  handleEditClick = () => {
    this.props.buttonClicked({ name: 'edit_profile_click' });
    this.setState({ view: VIEW_PROFILE_EDIT });
  };

  upload = (field, file) => {
    resizeImage(file).then(({ dataUrl }) => {
      this.props.uploadStarted({ type: TYPE_IMAGE });
      this.props.change('profile', field, dataUrl);
      this.props.upload(dataUrl, TYPE_IMAGE, file => {
        this.props.uploadEnded({ type: TYPE_IMAGE });
        return change('profile', field, file);
      });
    });
  };

  handleImageChange = file => this.upload('image', file);
  handleCoverChange = file => this.upload('cover', file);

  handleCancel = () => {
    this.setState({ view: VIEW_PROFILE });
    this.props.reset('profile');
    this.props.cancelUploads();
  };

  handleSubmit = data => {
    const { user } = this.state;
    this.setState({ view: VIEW_PROFILE, disabledUpdate: true });
    this.props
      .updateUser(user._id, data)
      .then(user => this.props.replace(`/profiles/${user.slug}`))
      .then(() => this.setState({ disabledUpdate: false }))
      .then(() => this.props.showMessage(messages[PROFILE_UPDATE].content(), messages[PROFILE_UPDATE].type))
      .catch(() => {
        this.setState({ disabledUpdate: false, user: this.props.user });
        this.props.reset('profile');
        this.props.showMessage(messages[PROFILE_UPDATE_FAILED].content(), messages[PROFILE_UPDATE_FAILED].type);
      });
  };

  handleSubmitFail = errors => {
    this.props.showMessage(messages[REQUIRE_PROFILE_FIELDS].content(errors), messages[REQUIRE_PROFILE_FIELDS].type);
  };

  handleSubmitClick = () => this.form.submit();

  renderButton = () => {
    const { user, isCurrentUser, view } = this.state;
    const { t } = this.props;

    if (isCurrentUser) {
      return view === VIEW_PROFILE ?
        <Button
          className="profile-page__edit-button"
          onClick={this.handleEditClick}
          data-hook="profile-page__edit-button"
        >
          {t('profile-page.edit-profile')}
        </Button> : [
          <Button
            key="cancel"
            className={classNames(profileStyles.cancelButton)}
            onClick={this.handleCancel}
            isSecondary
            data-hook="profile-page__cancel-button"
          >
            {t('profile.cancel')}
          </Button>,
          <Button
            key="confirm"
            className="profile-page__submit-button"
            onClick={this.handleSubmitClick}
            data-hook="profile-page__submit-button"
          >
            {t('profile.confirm')}
          </Button>,
        ];
    }

    return (
      <FollowUserButtonSecure user={user}>
        <PublicProfileActions triggerButton={ProfileActionsButton} user={user}/>
      </FollowUserButtonSecure>
    );
  };

  renderProfile = () => {
    const { user } = this.state;
    if (user._id) {
      return (
        <Profile
          key="profile"
          ref={this.setForm}
          view={this.state.view}
          user={user}
          initialValues={user}
          onImageChange={this.handleImageChange}
          onCoverChange={this.handleCoverChange}
          onSubmit={this.handleSubmit}
          onSubmitFail={this.handleSubmitFail}
        >
          {this.renderButton()}
        </Profile>
      );
    }

    return (
      <div>
        <ProfilePlaceholder key="placeholder"/>
      </div>
    );
  };

  render = () => {
    const { user } = this.state;
    const { children } = this.props;

    return (
      <Page noSpacing>
        <PageMetadata title={user.name} type="profile" noIndex/>
        <ProfilePageLayout profile={this.renderProfile()}>
          {children}
        </ProfilePageLayout>
        <AppLoaded/>
      </Page>
    );
  };
}

ProfilePage.propTypes = {
  children: PropTypes.node,
  replace: PropTypes.func.isRequired,
  isOwner: PropTypes.bool,
  showMessage: PropTypes.func,
  buttonClicked: PropTypes.func.isRequired,
  user: PropTypes.object,
  isCurrentUser: PropTypes.bool,
  params: PropTypes.object.isRequired,
  userSlug: PropTypes.string.isRequired,
  logout: PropTypes.func,
  t: PropTypes.func,
  promotePrivileges: PropTypes.func,
  demotePrivileges: PropTypes.func,
  upload: PropTypes.func,
  cancelUploads: PropTypes.func,
  change: PropTypes.func,
  reset: PropTypes.func,
  updateUser: PropTypes.func,
  pageOpened: PropTypes.func,
  uploadStarted: PropTypes.func.isRequired,
  uploadEnded: PropTypes.func.isRequired,
};

ProfilePage.defaultProps = {
  user: {},
  currentPagePosts: [],
  allPosts: [],
};

const mapStateToProps = (state, ownProps) => {
  const userSlug = ownProps.params.userSlug;
  const currentUserSlug = getCurrentUserSlug(state);
  const isCurrentUser = currentUserSlug === userSlug;
  const user = getUserBySlug(state, userSlug) || {};

  return {
    user,
    isCurrentUser,
    userSlug,
    isOwner: isOwner(state),
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      replace,
      upload,
      cancelUploads,
      change,
      reset,
      pageOpened,
      buttonClicked,
      updateUser,
      uploadStarted,
      uploadEnded,
    },
    dispatch
  );

export default _.flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withEnvironment({ showMessage: 'showMessage' }),
  withTranslate
)(ProfilePage);



// WEBPACK FOOTER //
// ./app/containers/profile-page/profile-page.js